<script setup>
/**
 * Sends an SMS or WhatsApp campaign to one number so the author can read it
 * on a real handset before it goes to the whole audience. The send bypasses
 * the campaign's audience entirely and leaves its status alone.
 */
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  selectedCampaign: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const phoneNumber = ref('');
const isSending = ref(false);

// Deliberately loose, matching the backend: enough to catch an empty or
// obviously-not-a-number field without rejecting real international formats.
const isValidPhoneNumber = computed(() =>
  /^\+?[0-9][0-9\s\-().]{5,19}$/.test(phoneNumber.value.trim())
);

// Each campaign gets a fresh field rather than inheriting the last one tested.
watch(
  () => props.selectedCampaign?.id,
  () => {
    phoneNumber.value = '';
  }
);

const handleConfirm = async () => {
  if (!isValidPhoneNumber.value) return;

  isSending.value = true;
  try {
    await store.dispatch('campaigns/sendTest', {
      id: props.selectedCampaign.id,
      phoneNumber: phoneNumber.value.trim(),
    });
    useAlert(t('CAMPAIGN.ALL.SEND_TEST.SUCCESS_MESSAGE'));
    dialogRef.value?.close();
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('CAMPAIGN.ALL.SEND_TEST.ERROR_MESSAGE')
    );
  } finally {
    isSending.value = false;
  }
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="sm"
    :title="t('CAMPAIGN.ALL.SEND_TEST.TITLE')"
    :description="
      t('CAMPAIGN.ALL.SEND_TEST.DESCRIPTION', {
        title: selectedCampaign?.title ?? '',
      })
    "
    :confirm-button-label="t('CAMPAIGN.ALL.SEND_TEST.CONFIRM')"
    :disable-confirm-button="!isValidPhoneNumber"
    :is-loading="isSending"
    @confirm="handleConfirm"
  >
    <Input
      v-model="phoneNumber"
      type="tel"
      :label="t('CAMPAIGN.ALL.SEND_TEST.PHONE_NUMBER.LABEL')"
      :placeholder="t('CAMPAIGN.ALL.SEND_TEST.PHONE_NUMBER.PLACEHOLDER')"
      :message="t('CAMPAIGN.ALL.SEND_TEST.PHONE_NUMBER.HINT')"
      message-type="info"
    />
  </Dialog>
</template>
